import { ForbiddenError, UnauthorizedError, errorCodes } from "@exceptions";
import { useEndSession } from "@src/hooks/use-end-session";
import { toSafeInternalPath } from "@src/utils/safe-internal-path";
import {
  MutationCacheNotifyEvent,
  QueryCacheNotifyEvent,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";

import {
  LOGIN_PATH,
  VERIFY_TWO_FA_PATH,
  VERIFY_YOUR_EMAIL,
} from "../modules/main/router/paths";
import { toMemberPath } from "../path";
import { useMe } from "./me";

export type SignInStep = "login" | "twoFactor" | "verifyEmail";

const SIGN_IN_STEP_PATHS: Record<SignInStep, () => string> = {
  login: LOGIN_PATH,
  twoFactor: VERIFY_TWO_FA_PATH,
  verifyEmail: VERIFY_YOUR_EMAIL,
};

export function signInStepFor(error: unknown): SignInStep | undefined {
  if (error instanceof UnauthorizedError) {
    return error.errorCode === errorCodes.TWO_FACTOR_AUTH_REQUIRED
      ? "twoFactor"
      : "login";
  }

  if (
    error instanceof ForbiddenError &&
    error.errorCode === errorCodes.EMAIL_VERIFICATION_REQUIRED
  ) {
    return "verifyEmail";
  }

  return undefined;
}

export function signInStepPath(step: SignInStep): string {
  return toMemberPath(SIGN_IN_STEP_PATHS[step]());
}

export function returnPathOf(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function useAuthenticated(onSuccessRedirectTo?: string) {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const endSession = useEndSession();
  const currentPath = returnPathOf(location);

  useEffect(() => {
    const returnTo = onSuccessRedirectTo ?? currentPath;
    const options = { state: { from: returnTo } };

    const leaveFor = (step: SignInStep) => {
      if (step === "verifyEmail") {
        navigate(signInStepPath(step), options);
      } else {
        endSession(signInStepPath(step), options);
      }
    };

    const queryCacheUnsubscribe = queryClient
      .getQueryCache()
      .subscribe((event: QueryCacheNotifyEvent) => {
        if (event.query.state.status === "error" && event.type === "updated") {
          const step = signInStepFor(event.query.state.error);
          if (step) leaveFor(step);
        }
      });

    const mutationCacheUnsubscribe = queryClient
      .getMutationCache()
      .subscribe((event: MutationCacheNotifyEvent) => {
        if (
          event?.mutation?.state.status === "error" &&
          event.mutation.state.error instanceof UnauthorizedError
        ) {
          const step = signInStepFor(event.mutation.state.error);
          if (step) leaveFor(step);
        }
      });

    return () => {
      queryCacheUnsubscribe();
      mutationCacheUnsubscribe();
    };
  }, [queryClient, endSession, navigate, currentPath, onSuccessRedirectTo]);
}

export function useMeAuthenticated(returnPath?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string } | undefined;
  const from = returnPath ?? state?.from;

  const {
    isSuccess: meIsSuccess,
    isError: meIsError,
    error: meError,
    dataUpdatedAt: meUpdatedAt,
    isLoading: meIsLoading,
  } = useMe();

  useEffect(() => {
    if (meIsSuccess) {
      navigate(from ? toSafeInternalPath(from) : "/");
    } else if (meIsError) {
      const step = signInStepFor(meError);
      if (step && step !== "login") {
        navigate(signInStepPath(step), { state: { from } });
      }
    }
  }, [meUpdatedAt, meIsError]);

  return {
    meIsSuccess,
    meIsError,
    meError,
    meUpdatedAt,
    meIsLoading,
  };
}
